import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { FeatureGroupService } from 'src/app/shared/services/featuregroup.service';
import { Constants } from '../constants';
import { UserAuthResponseModel, UserAuthResponseModelMain, UserAuthTokenResponse } from '../models/response/access-token-response-model';
import { Result, ResultOfT } from '../models/response/result';
import { AuthenticationRequest } from '../models/request/authentication-request-model';
import { ConfigResponseModel } from '../models/response/client-config-response';

import { BaseService } from './base.service';
import { WebStorageService } from './web-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService extends BaseService {
  private isSessionAlive: boolean;
  private unsubscriber = new Subject<boolean>();
  private showToaster = new BehaviorSubject<boolean>(true);
  public showToaster$: Observable<boolean> = this.showToaster.asObservable();

  private allowGroupAppointment = new BehaviorSubject<boolean>(false);
  public allowGroupAppointment$: Observable<boolean> = this.allowGroupAppointment.asObservable();

  loading: boolean;
  constructor(httpService: HttpClient, private router: Router, webStorageService: WebStorageService, private featureGroupService: FeatureGroupService) {
    super(httpService, webStorageService);

    this.isSessionAlive = true;
  }

  setAllowGroupAppointment(value: boolean) {
    this.allowGroupAppointment.next(value);
  }

  getgetAllowGroupAppointment() {
    return this.allowGroupAppointment.getValue();
  }

  isAuthenticated(redirectToSignIn: boolean = false) {
    const currentUserToken = this.webStorageService.getCurrentUserToken();
    if (currentUserToken) {
      const currentDate = new Date();
      const expiresOn = new Date(currentUserToken.expires);
      if (expiresOn < currentDate) {
        return false;
      }

      this.isSessionAlive = true;
      return true;
    } else {
      /*
        When user has opened website in two tabs and user do sign out manually
        from one tab and in the other tab trying to access any resource at that time
        token won't be found in local storage but token is required because accessing
        few property of token so whenever this kind of scenario comes in picture
        redirect to sign in page to prevent error cause
      */

      return false;
    }
  }

  getCurrentUserToken(): UserAuthResponseModel {
    return this.webStorageService.getCurrentUserToken();
  }

  getCurrentUserAuthToken(): UserAuthTokenResponse {
    return this.webStorageService.getCurrentUserAuthToken();
  }

  signIn(requestModel: AuthenticationRequest) {
    // Encode username and password as Base64 strings
    const userresponse: AuthenticationRequest = {
      ...requestModel,
      userName: btoa(requestModel.userName || ''),
      password: btoa(requestModel.password || '')
    };

    return this.post<ResultOfT<UserAuthResponseModelMain>>('authentication/authenticate', userresponse);
  }

  getClinetDetails(domainUrl: string, source: number) {
    if (!this.webStorageService.getClientConfig()) {
      return this.get<ResultOfT<ConfigResponseModel>>(
        'authentication/config?url=' + domainUrl + '&rid=' + Math.random().toString() + '&source=' + source,
        new HttpHeaders({
          'Content-Type': 'application/json',
          clientUrl: domainUrl
        })
      );
    } else {
      return this.get<ResultOfT<ConfigResponseModel>>(
        'authentication/config?url=' + domainUrl + '&rid=' + Math.random().toString() + '&source=' + source,
        new HttpHeaders({
          'Content-Type': 'application/json',
          clientId: this.webStorageService.getClientConfig().companyGuid
        })
      );
    }
  }
  getClinetDetailsCustom(domainUrl: string) {
    if (!this.webStorageService.getClientConfig()) {
      return this.get<ResultOfT<ConfigResponseModel>>(
        'authentication/configcustom?url=' + domainUrl + '&rid=' + Math.random().toString(),
        new HttpHeaders({
          'Content-Type': 'application/json',
          clientUrl: domainUrl,
          forceRefresh: 'true'
        })
      );
    } else {
      return this.get<ResultOfT<ConfigResponseModel>>(
        'authentication/configcustom?url=' + domainUrl + '&rid=' + Math.random().toString(),
        new HttpHeaders({
          'Content-Type': 'application/json',
          clientId: this.webStorageService.getClientConfig().companyGuid,
          forceRefresh: 'true'
        })
      );
    }
  }

  public setShowToaster(value: boolean) {
    this.showToaster.next(value);
  }

  goToLogin(redirectUrl?: string) {
    this.loading = false;
    this.setShowToaster(false);
    this.webStorageService.deleteCurrentUserToken();
    this.webStorageService.deleteIsSession();
    this.webStorageService.deleteIsSessionValue();
    this.webStorageService.deleteRandomNumber();
    this.webStorageService.deleteFeatureGroups();
    this.webStorageService.deleteUserBranch();
    this.webStorageService.deleteSelectedFacilies();
    this.webStorageService.deleteAppointmentConfirmation();
    this.webStorageService.deletePermissions();
    this.webStorageService.deleteDateFormat();
    this.webStorageService.deleteGlobalSearch();
    this.webStorageService.deleteSearchedRequestModel();
    this.webStorageService.deleteUserBrances();
    this.webStorageService.removedefaultModeofAppointment();
    this.webStorageService.deleteBrightreeSettings();
    this.webStorageService.deleteClientConfig();

    this.router.navigateByUrl('/login', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/login']);
    });
  }

  signOut(redirectUrl: string = null) {
    localStorage.setItem(Constants.userLogoutStateKey, 'true');
    this.featureGroupService
      .logout()
      .pipe(
        takeUntil(this.unsubscriber),
        finalize(() => this.goToLogin(redirectUrl))
      )
      .subscribe();
  }

  notAuthorized() {
    this.router.navigate(['/401']);
  }

  getRedirectUrlAfterSignIn(userType: string, url: string) {
    let webStore = this.webStorageService.getClientConfig();
    if (userType === Constants.userType.Therapist) {
      return '/therapist/my-appointments-new';
    } else if (userType) {
      return '/admin/' + url;
    } else {
      return '/login';
    }
  }
}
